import React, { useEffect, useState } from 'react'
import ExpandableContainer from '../components/ui/expandable-container'
import { OroscopoResponseJSON } from '../types/output-data'
import { getOroscopoResponse } from '../services/backend-services'
import { extractOroscopoResponseDataFromJSON } from '../utils/extract-data-from-json'
import SignalsService from '../services/communication/signals.service'
import { SIGNAL_EVENT, STAR_SIGN } from '../types/enums'
import PostMessageService, { PostMessageType } from '../services/communication/post-message.service'
import { getDateFormatted } from '../utils/date-utils'
import StarSign from '../components/ui/star-sign'
import ErrorMessage from '../components/ui/error-message'
import Resizer from '../components/ui/resizer'
import Number from '../components/ui/number'

/** Pagina dell'oroscopo
 *
 * @desc Questa pagina permette di cliccare sui segni zodiacali e ottenere l'oroscopo del giorno e i numeri consigliati da giocare associati al giorno e al segno zodiacale
 *
 */
const NumeriFortunatiOroscopo = () => {
  const starSigns = [
    STAR_SIGN.ariete,
    STAR_SIGN.toro,
    STAR_SIGN.gemelli,
    STAR_SIGN.cancro,
    STAR_SIGN.leone,
    STAR_SIGN.vergine,
    STAR_SIGN.bilancia,
    STAR_SIGN.scorpione,
    STAR_SIGN.sagittario,
    STAR_SIGN.capricorno,
    STAR_SIGN.acquario,
    STAR_SIGN.pesci
  ]

  // necessario per "evidenziare" il segno attualmente cliccato
  const [clickedSignIndex, setClickedSignIndex] = useState<number>(-1)

  const [numberResponse, setNumberResponse] = useState<OroscopoResponseJSON>({
    numbers: [],
    description: ''
  })

  const [showError, setShowError] = useState(false)

  const oroscopoTestId = 'oroscopo'
  const showNumber: boolean = !!(
    numberResponse &&
    numberResponse.numbers &&
    numberResponse.numbers.length > 0
  )

  const handleStarSignClick = async (starSign: STAR_SIGN) => {
    try {
      setShowError(false)
      SignalsService.fire(SIGNAL_EVENT.showLoader, 'flex')
      setClickedSignIndex(starSigns.indexOf(starSign))
      const responseJson = await getOroscopoResponse(starSign, getDateFormatted())
      const response = extractOroscopoResponseDataFromJSON(responseJson)
      setNumberResponse(response)
    } catch (e) {
      if (e instanceof Error) {
        console.error('Numeri Associati ai nomi:', e.message)
        setNumberResponse({
          numbers: [],
          description: ''
        })
        setShowError(true)
      }
    } finally {
      SignalsService.fire(SIGNAL_EVENT.showLoader, 'hidden')
    }
  }

  useEffect(() => {
    if (numberResponse.numbers && numberResponse.numbers.length > 0) {
      PostMessageService.send({
        type: PostMessageType.PLAY_NUMBERS,
        numbers: numberResponse.numbers
      })
    } else {
      PostMessageService.send({
        type: PostMessageType.RESET_NUMBERS
      })
    }
  }, [numberResponse])

  return (
    <Resizer idResizer={oroscopoTestId}>
      <div className={'w-full h-full text-base py-5 flex flex-col bg-lightOrangeColor'}>
        <form>
          <div className={'py-5'}>
            <div className={'w-full flex flex-col px-3.7 py-5 mobile:flex'}>
              <div className={'px-3.7 w-full'}>
                <p>Scegli il tuo segno</p>
              </div>

              <div
                className={'px-3.7 py-5 grid grid-cols-12 tablet:grid-cols-4 mobile:grid-cols-3'}>
                {starSigns.map((currStarSign, currStarSignIdx) => {
                  return (
                    <div key={currStarSignIdx} className={'px-3.7 flex flex-col items-center'}>
                      <StarSign
                        starSignName={currStarSign}
                        parentHandleClick={() => handleStarSignClick(currStarSign)}
                        isClicked={currStarSignIdx === clickedSignIndex}
                      />
                      <p id={currStarSign + '-name'} className={'w-full flex justify-center'}>
                        {currStarSign}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
            <ExpandableContainer
              id={oroscopoTestId}
              expandableCondition={showNumber}
              className={`transition-vertical-spacing duration-1000 ease-in-out overflow-hidden px-3.7 ${!showNumber ? '' : `py-5 mt-2.75`}`}
              tot_padding_y={20}
              tot_margin_y={11}>
              <div className={'flex mobile:flex-col gap-x-8 px-3.7'}>
                <div
                  id={oroscopoTestId + '-numbers-container'}
                  className={
                    'flex w-1/2 mobile:w-full gap-7 mobile:gap-4 mobile:mb-5 pl-3.7 mobile:pl-0'
                  }>
                  {numberResponse?.numbers?.map((numero, idx) => (
                    <Number
                      key={`${oroscopoTestId}-${numero}-${idx}`}
                      idNumber={`${oroscopoTestId}-${idx}`}
                      numberTxt={numero}
                      customClassName={`h-[50px] mobile:min-w-10 desktop:w-[50px] tablet:w-[40px] mobile:flex-grow flex-grow`}
                    />
                  ))}
                </div>
                <div
                  id={oroscopoTestId + '-description'}
                  className={'w-1/2 mobile:w-full pr-3.7 mobile:pr-0'}>
                  <p>{numberResponse?.description}</p>
                </div>
              </div>
            </ExpandableContainer>
          </div>
        </form>
      </div>
      <ErrorMessage idError={oroscopoTestId} isVisible={showError} />
    </Resizer>
  )
}

export default NumeriFortunatiOroscopo
